import { AnalyticsBrowser } from '@segment/analytics-next';
import apolloClient from 'apollo/sofarApolloClient';

import {
  ConfirmPaymentIntentApiResponse,
  ConfirmPaymentParameters,
  CreatePaymentIntentApiResponse,
} from 'app/shared/typings';
import {
  SEGMENT_CDN_URL,
  SEGMENT_WRITE_KEY,
} from 'app/shared/context/Analytics';
import { GENERIC_ERROR } from 'app/fan/utils/checkout';
import { CONFIRM_PAYMENT_INTENT } from 'app/fan/graphql/payments/mutations';

/**
 * This method will only be triggered if the user has to confirm their payment
 * manually for SCA / 3d Secure Checkout
 *
 * If this is the case we need to make another request to our api that sets the
 * payment intent (created earlier) to confirmed
 */
const confirmPayment = async (
  stripe: any,
  response: CreatePaymentIntentApiResponse,
  billingRegion: string
) => {
  const analytics = AnalyticsBrowser.load(
    {
      writeKey: SEGMENT_WRITE_KEY,
      cdnURL: SEGMENT_CDN_URL,
    },
    {
      cookie: {
        maxage: 90, // 90 days
      },
    }
  );

  const {
    createPaymentIntent: {
      paymentIntentClientSecret,
      user,
      success,
      requiresAction,
      paymentIntentId,
    },
  } = response;

  analytics.track('Confirm Payment Secure Checkout', {
    user,
    success,
    requiresAction,
    paymentIntentId,
  });

  return stripe
    .handleCardAction(paymentIntentClientSecret)
    .then(async (response: any) => {
      const { error, paymentIntent } = response;

      if (error) {
        analytics.track('Confirm Payment Handle Card Action Failure', {
          user,
          success,
          requiresAction,
          paymentIntentId,
          error,
        });
        throw new Error(error.message);
      } else {
        const confirmPayment = await apolloClient.mutate<
          ConfirmPaymentIntentApiResponse,
          ConfirmPaymentParameters
        >({
          mutation: CONFIRM_PAYMENT_INTENT,
          variables: {
            paymentIntentId: paymentIntent.id,
            billingRegion,
          },
        });

        if (confirmPayment.data) {
          const {
            /**
             * See note on ConfirmPaymentIntentApiResponse for why the
             * key is createPaymentIntent and not confirmPaymentIntent
             */
            confirmPaymentIntent: { paymentIntentId, success },
          } = confirmPayment.data;

          if (success) {
            return paymentIntentId;
          } else {
            analytics.track('Confirm Payment Handle Card Action Failure', {
              user,
              success,
              requiresAction,
              paymentIntentId,
              confirmPayment,
            });
            throw new Error(GENERIC_ERROR);
          }
        } else {
          analytics.track('Confirm Payment Handle Card Action Failure', {
            user,
            success,
            requiresAction,
            paymentIntentId,
            confirmPayment,
          });
          throw new Error(GENERIC_ERROR);
        }
      }
    })
    .catch((e: any) => {
      analytics.track('Confirm Payment Handle Card Action Failure', {
        user,
        success,
        requiresAction,
        paymentIntentId,
        error: e,
      });
      throw new Error(e);
    });
};

export default confirmPayment;
